/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

/**
 * @hook useHasMounted
 *
 * Provides a utility hook for determining if a component has mounted. This hook will
 * always resolve to one of three values; either null (component hasn't mounted, true,
 * the component has mounted, or false, the component has been unmounted). Each of the
 * three states can be used independently as needed. For example, a component may wish
 * to invoke asynchronous services only when it has yet to be mounted, or is mounted,
 * but not when it is unmounted, etc..
 *
 * An optional onMount and onUnmount callbacks can be included to be informed of each
 * mounting state, respectively.
 *
 * ```typescript
 *
 *   import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
 *
 *   const SomeComponent = () => {
 *     // react component mount changes ...
 *     const isMounted = useHasMounted();
 *     if (isMounted && !isInitialized) {
 *       ...
 *     }
 *   }
 *
 *   const SomeOtherComponent = () => {
 *     // invoke functions when the component has mounted and unmounted ...
 *     const isMounted = useHasMounted(
 *       onMounted,
 *       onUnmounted
 *     );
 *   }
 *
 * ```
 */
export function useHasMounted(onMount?: Function, onUnmount?: Function) {
  const [hasMounted, setHasMounted] = useState<boolean | null>(null);
  useEffect(() => {
    onMount?.();
    setHasMounted(true);
    return () => {
      onUnmount?.();
      setHasMounted(false);
    };
  }, []);
  return hasMounted;
}
export default useHasMounted;
